import type { GetServerSidePropsContext } from 'next'
import Cookie from 'cookie-universal'
import {
  LAST_VIEWED_CLASS_COOKIE,
  ALL_VIEWED_CLASSES_COOKIE,
} from '@mc/constants'
import { EventTypes, track, Locations, getPageSource } from '@mc/track-event'
import { ONE_YEAR } from 'libs/constants'

const COOKIE_OPTIONS = {
  maxAge: ONE_YEAR,
  path: '/',
}

type Req = GetServerSidePropsContext['req']
type Res = GetServerSidePropsContext['res']
type Cookies = ReturnType<typeof Cookie>

const setLastCmPageViewed = (cookies: Cookies, classSlug: string) => {
  cookies.set(LAST_VIEWED_CLASS_COOKIE, classSlug, COOKIE_OPTIONS)
}

const addViewedCmPage = (cookies: Cookies, classSlug: string) => {
  const viewedClasses = new Set(cookies.get(ALL_VIEWED_CLASSES_COOKIE) || [])

  viewedClasses.add(classSlug)

  cookies.set(
    ALL_VIEWED_CLASSES_COOKIE,
    JSON.stringify([...viewedClasses]),
    COOKIE_OPTIONS,
  )
}

export const trackViewedClass = (req: Req, res: Res, slug: string) => {
  const cookies = Cookie(req, res)

  setLastCmPageViewed(cookies, slug)
  addViewedCmPage(cookies, slug)
}

export const ClassPageLocations = {
  EMAIL_CAPTURE: 'Email Capture',
  ONE_MEMBERSHIP: 'One membership. Infinite possibilities',
  MEMBER_STORIES: 'Member Stories',
  SKILLS_YOU_LEARN: "Skills You'll Learn",
  START_YOUR_JOURNEY_TODAY: 'Start your journey today',
  TRAILER_SAMPLE: 'Trailer and Sample',
  COURSES_CAROUSEL: 'From here, go anywhere',
  MEMBERSHIP_SECTION: 'Membership Section',
  CATEGORY_PICKER: 'Category Picker', // pen-7401_v1 — remove when the experiment is cleaned up
} as const

export type ClassPageLocation =
  (typeof ClassPageLocations)[keyof typeof ClassPageLocations]

// Values feed the `location` of the "Purchase Click" fired by the CTA inside the
// trailer modal, and of "Trailer Deeplink Playback Started"; changing one breaks
// their dashboards.
export const TrailerModalLocations = {
  HERO: 'hero_trailer_modal',
  DEEPLINK: Locations.TRAILER_DEEPLINK_MODAL,
} as const

export type TrailerModalLocation =
  (typeof TrailerModalLocations)[keyof typeof TrailerModalLocations]

export const trackTrailerActionClicked = ({ slug }: { slug: string }) =>
  track(EventTypes.COURSE_MARKETING_CLICK, {
    action: 'Trailer',
    class: slug,
    category: 'class-navigation',
    location: Locations.HERO,
    page_path: window.location.pathname,
    page_category: 'class',
  })

export const trackTrailerDeeplinkPlaybackStarted = ({
  slug,
}: {
  slug: string
}) =>
  track(EventTypes.TRAILER_DEEPLINK_PLAYBACK_STARTED, {
    class: slug,
    location: TrailerModalLocations.DEEPLINK,
    page_path: window.location.pathname,
    page_category: 'class',
    page_source: getPageSource(),
  })

export const trackLessonPopoverClosed = () =>
  track(EventTypes.COURSE_MARKETING_CLICK, {
    action: 'close',
    location: 'View lesson plan popover',
  })

export const trackViewLessonPlanClicked = () =>
  track(EventTypes.COURSE_MARKETING_CLICK, {
    action: 'View Lesson Plan',
    location: ClassPageLocations.SKILLS_YOU_LEARN,
  })

export const trackPageSectionViewed = (location: string) =>
  track('Page Section Viewed', {
    location,
    page_source: getPageSource(),
  })

export const trackSkillsYoullLearnViewed = (props?: {
  location?: number
  cardTitle?: string
}) =>
  track(`${ClassPageLocations.SKILLS_YOU_LEARN} Card Viewed`, {
    location: props?.location,
    cardTitle: props?.cardTitle,
  })

export const trackMemberStoriesArrowClick = (arrow: 'left' | 'right') =>
  track(EventTypes.COURSE_MARKETING_CLICK, {
    action: arrow,
    location: ClassPageLocations.MEMBER_STORIES,
  })

export const trackTrailerSampleToggled = (type: 'Trailer' | 'Sample') =>
  track(EventTypes.COURSE_MARKETING_CLICK, {
    action: type,
    location: ClassPageLocations.TRAILER_SAMPLE,
  })

export const trackCoursesCarouselTileClicked = () =>
  track(EventTypes.COURSE_MARKETING_CLICK, {
    action: 'click',
    location: ClassPageLocations.COURSES_CAROUSEL,
  })
